'use client';

import {
  BackDrop,
  Box,
  Button,
  IconButton,
  Portal,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { Sheet } from 'react-modal-sheet';

import {
  NewsletterSubscriptionCompleteGuideImage,
  OrDivider,
  SignupLink,
  SocialButton,
} from './NewsletterSubscriptionCompleteDialog.parts';

import { useDeviceCheck } from '@/shared/hooks';

interface NewsletterSubscriptionCompleteDialogProps {
  email: string;
  isOpen: boolean;
  onClose: () => void;
}

interface NewsletterSubscriptionCompleteContentsProps {
  email: string;
  onClose: () => void;
}

const NewsletterSubscriptionCompleteContents = ({
  email,
  onClose,
}: NewsletterSubscriptionCompleteContentsProps) => {
  return (
    <>
      <Box
        data-testid="newsletter-subscription-complete-contents"
        className="relative flex w-full flex-col items-center gap-3"
      >
        <IconButton
          className="absolute right-0 top-0 hidden desktop:block"
          onClick={onClose}
        >
          <SystemIcon name="large_delete" className="text-w-gray-600" />
        </IconButton>
        <NewsletterSubscriptionCompleteGuideImage />
        <Typography
          variant="subTitle1"
          className="whitespace-pre-line text-center font-medium text-w-gray-900"
        >
          {'뉴스레터 구독 완료! \n요즘IT 멤버가 되어 스크랩해 보세요.'}
        </Typography>
        <Typography
          variant="body1"
          className="hidden text-center text-w-gray-500 desktop:block"
        >
          실무에 도움 되는 콘텐츠를 필요할 때 꺼내볼 수 있어요.
        </Typography>
      </Box>
      <Box className="flex w-full flex-col items-center gap-4">
        <Box onClick={onClose} className="w-full">
          <SocialButton icon="social_google" text="구글로 회원가입" />
        </Box>
        <Box onClick={onClose} className="w-full">
          <SocialButton icon="social_facebook" text="페이스북으로 회원가입" />
        </Box>
        <OrDivider />
        <SignupLink email={email}>
          <Button variant="solid" className="w-full" onClick={onClose}>
            회원가입
          </Button>
        </SignupLink>
      </Box>
    </>
  );
};

const NewsLetterCompleteModal = ({
  email,
  onClose,
}: NewsletterSubscriptionCompleteContentsProps) => {
  return (
    <Portal rootId="portal">
      <Box className="fixed top-0 z-30 hidden h-full w-full items-center justify-center desktop:flex">
        <BackDrop isBlack onClick={onClose} />
        <Box className="z-40 flex w-[500px] flex-col gap-8 rounded-3xl border border-w-gray-100 bg-w-white p-8 shadow-graymedium">
          <NewsletterSubscriptionCompleteContents
            onClose={onClose}
            email={email}
          />
        </Box>
      </Box>
    </Portal>
  );
};

const NewsletterSubscriptionCompleteDialog = ({
  email,
  isOpen,
  onClose,
}: NewsletterSubscriptionCompleteDialogProps) => {
  const { isDesktop, isLoading } = useDeviceCheck();

  if (isLoading) return <></>;

  return (
    <>
      {isOpen && <NewsLetterCompleteModal email={email} onClose={onClose} />}
      {isDesktop || (
        <Sheet
          isOpen={isOpen}
          onClose={onClose}
          className="desktop:hidden"
          detent="content-height"
        >
          <Sheet.Container className="react-modal-sheet-container">
            <Sheet.Header />
            <Sheet.Content className="gap-6 px-5 pb-8 pt-5">
              <NewsletterSubscriptionCompleteContents
                onClose={onClose}
                email={email}
              />
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop onTap={onClose} />
        </Sheet>
      )}
    </>
  );
};

export default NewsletterSubscriptionCompleteDialog;
